

























































































































































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Editor, EditorContent, JSONContent } from '@tiptap/vue-2';
import EditorOption from '@/components/question-editor/EditorOption.vue';
import extentions from '@/components/question-editor/editor-extensions/editor-extensions';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import EditorMenubar from '@/components/question-editor/new-editor/EditorMenubar.vue';
import Icon from '@/main-components/common/Icon.vue';
import MainButton from '@/main-components/common/MainButton.vue';
import Form, { InputData } from '@/main-components/common/Form.vue';
import Modal from '@/main-components/common/modal/Modal.vue';
import getModifiedJson from '../editor-output/get-modified-json';
import { QuestionLanguage } from '@/resource/subject/subject.model';
import {
  AnswerHtml,
  PdfHtml,
  Question,
  QuestionHtml,
  QuestionObjective,
  QuestionSubjective,
} from '@/resource/question/question.model';
import {
  addQuestionByUser,
  AddQuestionByUserParams,
  addQuestionToPaper,
  AddQuestionToPaperParams,
  BuildObjectiveQuestion,
  buildObjectiveQuestionEmpty,
  BuildSubjectiveQuestion,
  buildSubjectiveQuestionEmpty,
  deleteQuestions,
  publishQuestions,
  saveQuestion,
  SaveQuestionParams,
  unpublishQuestions,
  updateQuestionByUser,
  UpdateQuestionByUserParams,
} from '@/utils/add-question/add-question-util';
import { SaveQuestionEvent } from '@/resource/question/question.store';
import EditorPreview from './EditorPreview.vue';
import {
  editorInitInputs,
  adminEditorInitInputs,
  updateEditorInputsByQuestion,
  randomizeObjAnswerPosition,
  convertOldJSONSUbToHTML,
} from './new-editor.util';
import ModalConfirm from '@/main-components/common/modal/ModalConfirm.vue';
import { AnswerSpaceType } from '../editor-extensions/nodes/node-answer-space';

interface AnswerEditor {
  id: string;
  editor: Editor | null;
  error: boolean | string;
}

@Component({
  components: {
    EditorContent,
    EditorOption,
    EditorMenubar,
    EditorPreview,
    Icon,
    MainButton,
    Form,
    Modal,
    ModalConfirm,
  },
})
export default class EditorStudio extends Vue {
  editor: null | Editor = null;
  editorVersion = '1.1.0';
  isOutputLoading = false;
  isDraft = false;
  showPreview = false;
  isCreateNewQuestion = true;
  isRedirectPageLoading = false;
  isDeleteButtonDisabled = true;
  isDuplicateQuestion = false;
  languages: QuestionLanguage[] = [];

  @Prop({ default: false })
  isAdmin!: boolean;

  @Prop({ default: false })
  showEditor!: boolean;

  @Prop({ default: false })
  isSubjective!: boolean;

  @Prop({ default: null })
  question!: Question | null;

  @Prop({ default: null })
  paperUuid!: string | null;

  @Prop({ default: null })
  paperItemId!: number | null;

  inputs: InputData[] = [];

  created() {
    this.initEditor();
  }

  // Subjective Items
  subQuestionEditor: null | Editor = null;
  subQuestionError: boolean | string = false;

  // Objective Items
  objQuestionEditor: null | Editor = null;
  objQuestionError: boolean | string = false;

  answerEditorLists: AnswerEditor[] = [];

  buildSubjectiveQuestion: BuildSubjectiveQuestion = buildSubjectiveQuestionEmpty;
  buildObjectiveQuestion: BuildObjectiveQuestion = buildObjectiveQuestionEmpty;

  // Starter Kit Configuration
  starterKitConfig = StarterKit.configure({
    listItem: false,
    paragraph: false,
    orderedList: false,
  });

  // Placeholder Configuration
  placeholderConfig = (text: string) =>
    Placeholder.configure({
      placeholder: text,
    });

  // Default General Extension
  editorExtensions = [
    this.starterKitConfig,
    this.placeholderConfig('Mula menulis soalan subjective…'),
    ...extentions,
  ];

  // Question Editor Extension
  QEExtensions = [
    this.starterKitConfig,
    this.placeholderConfig('Mula menulis soalan…'),
    ...extentions,
  ];

  // Answer Editor Extension
  AEExtensions = [
    this.starterKitConfig,
    this.placeholderConfig('Mula menulis jawapan...'),
    ...extentions,
  ];

  @Watch('showEditor')
  initEditor() {
    if (!this.showEditor) return;

    const copyEditorInitInputs = editorInitInputs.map(editorInitInput =>
      JSON.parse(JSON.stringify(editorInitInput)),
    );

    if (this.isAdmin) {
      const copyAdminEditorInitInputs = adminEditorInitInputs.map(
        editorInitInput => JSON.parse(JSON.stringify(editorInitInput)),
      );

      this.inputs = [...copyEditorInitInputs, ...copyAdminEditorInitInputs];
    } else {
      this.inputs = [...copyEditorInitInputs];
    }

    if (this.question) {
      updateEditorInputsByQuestion(this.inputs, this.question);
      this.isDeleteButtonDisabled = !this.question.isDraft;
    }

    this.objQuestionError = false;
    this.subQuestionError = false;

    this.destroyAllEditors();

    // Default General Editor
    this.editor = new Editor({
      extensions: this.editorExtensions,
    });

    if (this.isSubjective) {
      // Subjective Question Editor
      const questionSubjective = this.question as QuestionSubjective;

      this.subQuestionEditor = new Editor({
        autofocus: true,
        extensions: this.QEExtensions,
        content: questionSubjective
          ? questionSubjective.questionJson
            ? questionSubjective.questionJson
            : questionSubjective.question
            ? convertOldJSONSUbToHTML(questionSubjective.question, null)
            : null
          : null,
        onFocus: () => {
          this.editor = this.subQuestionEditor;
        },
      });
    } else {
      const questionOjective = this.question as QuestionObjective;
      // Objective Question Editor
      this.objQuestionEditor = new Editor({
        autofocus: true,
        extensions: this.QEExtensions,
        content: this.question
          ? questionOjective.questionJson
            ? questionOjective.questionJson
            : questionOjective.question
          : null,
        onFocus: () => {
          this.editor = this.objQuestionEditor;
        },
      });

      let correctAnswer: JSONContent | string = '';
      let wrongAnswers: (JSONContent | string)[] = [];

      if (questionOjective) {
        if (
          questionOjective.answerWrongJsons &&
          questionOjective.answerCorrectJson
        ) {
          correctAnswer = questionOjective.answerCorrectJson;
          wrongAnswers = questionOjective.answerWrongJsons;
        } else if (
          questionOjective.correctAnswers &&
          questionOjective.wrongAnswers &&
          questionOjective.correctAnswers[0] &&
          questionOjective.wrongAnswers.length
        ) {
          correctAnswer = questionOjective.correctAnswers[0];
          wrongAnswers = questionOjective.wrongAnswers;
        }
      }

      if (correctAnswer && wrongAnswers && wrongAnswers.length) {
        const ansOrder = ['B', 'C', 'D', 'E'];

        this.answerEditorLists = wrongAnswers.map((answerWrongJson, index) => {
          return {
            id: ansOrder[index],
            editor: this.newObjAnswerEditor(false, answerWrongJson),
            error: false,
          };
        });

        this.answerEditorLists.unshift({
          id: 'A',
          editor: this.newObjAnswerEditor(true, correctAnswer),
          error: false,
        });
      } else {
        this.answerEditorLists = [
          {
            id: 'A',
            editor: this.newObjAnswerEditor(true),
            error: false,
          },
          {
            id: 'B',
            editor: this.newObjAnswerEditor(false),
            error: false,
          },
          {
            id: 'C',
            editor: this.newObjAnswerEditor(false),
            error: false,
          },
        ];
      }
    }
  }

  addAnswerSection() {
    this.subQuestionEditor &&
      this.subQuestionEditor
        .chain()
        .focus()
        .toggleAnswerSection(AnswerSpaceType.Empty)
        .run();
  }

  newObjAnswerEditor(
    isCorrectAnswer: boolean,
    content: JSONContent | string | null = null,
  ) {
    let newEditor: Editor | null = null;

    newEditor = new Editor({
      extensions: [
        this.starterKitConfig,
        this.placeholderConfig(
          isCorrectAnswer ? 'Jawapan betul...' : 'Jawapan salah...',
        ),
        ...extentions,
      ],
      content,
      onFocus: () => {
        this.editor = newEditor;
      },
    });

    return newEditor;
  }

  addMoreAnswer() {
    // TODO: Fix this. Just poc atm. Refactor
    const ansOrder = ['A', 'B', 'C', 'D', 'E'];

    const answerEditorListsLength = this.answerEditorLists.length;
    this.answerEditorLists.push({
      id: ansOrder[answerEditorListsLength],
      editor: this.newObjAnswerEditor(false),
      error: false,
    });
  }

  removeAnswer(removedEditor: AnswerEditor, removedEditorIndex: number) {
    const ansOrder = ['A', 'B', 'C', 'D', 'E'];

    if (removedEditor.id === 'A' || removedEditor.id === 'B') return;

    removedEditor.editor && removedEditor.editor.destroy();

    this.answerEditorLists.splice(removedEditorIndex, 1);

    this.answerEditorLists = this.answerEditorLists.map(
      (answerEditorList, index) => {
        answerEditorList.id = ansOrder[index];
        return answerEditorList;
      },
    );
  }

  closeEditor() {
    this.showPreview = false;
    this.$emit('closeQuestionEditor');
  }

  beforeDestroy() {
    this.destroyAllEditors();
  }

  destroyAllEditors() {
    // Destroy all created editor to avoid memory leaks
    this.editor?.destroy();
    this.objQuestionEditor?.destroy();
    this.answerEditorLists.forEach(answerEditorList => {
      answerEditorList.editor && answerEditorList.editor.destroy();
    });

    this.subQuestionEditor?.destroy();
  }

  openModal: any = null;
  closeModal: any = null;
  openDeleteQuestionModal: any = null;
  manualFormSubmitted: any = null;

  async openPreviewModalToDuplicate() {
    this.isCreateNewQuestion = true;
    this.isDuplicateQuestion = true;
    this.manualFormSubmitted && this.manualFormSubmitted();
  }

  async openPreviewModal(bodyRequest: any) {
    if (this.question && !this.isDuplicateQuestion) {
      this.isCreateNewQuestion = false;
    }

    this.showPreview = false;

    const chapterId = bodyRequest['subject'].selectedChapter;

    this.buildObjectiveQuestion.chapterId = chapterId;
    this.buildSubjectiveQuestion.chapterId = chapterId;

    if (this.isAdmin) {
      const languages = bodyRequest['languages'];
      const keywords = bodyRequest['keywords'] || '';
      const difficulty =
        bodyRequest['difficulty'] && parseInt(bodyRequest['difficulty']);
      const mainLanguage = bodyRequest['mainLanguage'];
      const isDraft = bodyRequest['isDraft'] === 'true' ? true : false;

      this.languages = languages;

      if (this.languages.length > 1) {
        this.languages = this.languages.filter(
          finalLanguage => finalLanguage !== mainLanguage,
        );

        this.languages.unshift(mainLanguage);
      }

      this.buildObjectiveQuestion.keywords = keywords;
      this.buildSubjectiveQuestion.keywords = keywords;

      this.buildObjectiveQuestion.difficulty = difficulty;
      this.buildSubjectiveQuestion.difficulty = difficulty;

      this.buildObjectiveQuestion.languages = this.languages;
      this.buildSubjectiveQuestion.languages = this.languages;

      this.isDraft = isDraft;
    } else {
      // NOTE: Make question created by user whould have both lang (malay & eng)
      this.languages = [QuestionLanguage.english, QuestionLanguage.malay];
      this.buildObjectiveQuestion.languages = this.languages;
      this.buildSubjectiveQuestion.languages = this.languages;
    }

    const hasError = this.checkForEmptyEditors();

    if (!hasError) {
      if (this.isSubjective) {
        await this.getSubjectiveOutput();
      } else {
        await this.getObjectiveOutput();
      }

      this.showPreview = true;
      this.openModal && this.openModal();
    }
  }

  openModalAdd(openModal: any) {
    this.openModal = openModal;
  }

  closeModalAdd(closeModal: any) {
    this.closeModal = closeModal;
  }

  formSubmittedAdd(formSubmitted: any) {
    this.manualFormSubmitted = formSubmitted;
  }

  openDeleteQuestionModalAdd(openModal: any) {
    this.openDeleteQuestionModal = openModal;
  }

  checkForEmptyEditors(): boolean {
    let hasError = false;

    if (this.isSubjective) {
      this.subQuestionError = this.subQuestionEditor?.isEmpty
        ? 'Tidak boleh kosong'
        : false;

      hasError = this.subQuestionError ? true : false;
    } else {
      this.objQuestionError = this.objQuestionEditor?.isEmpty
        ? 'Tidak boleh kosong'
        : false;

      hasError = this.objQuestionError ? true : false;

      this.answerEditorLists = this.answerEditorLists.map(answerEditorList => {
        const answerIsEmpty = answerEditorList.editor?.isEmpty;

        if (answerIsEmpty) {
          answerEditorList.error = 'Tidak boleh kosong';
          hasError = true;
        } else {
          answerEditorList.error = false;
        }

        return answerEditorList;
      });
    }

    return hasError;
  }

  async getObjectiveOutput() {
    if (this.isOutputLoading) return false;
    this.isOutputLoading = true;

    const questionJson = this.objQuestionEditor?.getJSON() as JSONContent;
    const questionHtmls: QuestionHtml[] = [];

    let answerCorrectJson: JSONContent | null = null;
    const answerWrongJsons: JSONContent[] = [];

    const answerCorrectHtmls: AnswerHtml[] = [];
    const answerWrongHtmls: AnswerHtml[][] = [];

    this.answerEditorLists.forEach((answerEditorList, index) => {
      const jsonGenerated = (answerEditorList.editor &&
        answerEditorList.editor.getJSON()) as JSONContent;

      if (index === 0) {
        answerCorrectJson = jsonGenerated;
      } else {
        answerWrongJsons.push(jsonGenerated);
      }
    });

    const newQuestionJsons = await getModifiedJson(
      questionJson,
      this.languages,
      true,
    );

    for (const newQuestionJson of newQuestionJsons) {
      const questionEditor = new Editor({
        extensions: this.QEExtensions,
        content: newQuestionJson.newJson,
      });

      const questionHtml = questionEditor.getHTML();

      questionHtmls.push({
        languages: newQuestionJson.languages,
        question: questionHtml,
      });
    }

    if (answerCorrectJson) {
      const withLangGeneratedJsons = await getModifiedJson(
        answerCorrectJson,
        this.languages,
        true,
      );

      for (const withLangGeneratedJson of withLangGeneratedJsons) {
        const answerEditor = new Editor({
          extensions: this.AEExtensions,
          content: withLangGeneratedJson.newJson,
        });

        const answerHtml = answerEditor.getHTML();

        answerCorrectHtmls.push({
          languages: withLangGeneratedJson.languages,
          answer: answerHtml,
        });
      }
    }

    for (const answerWrongJson of answerWrongJsons) {
      const withLangGeneratedJsons = await getModifiedJson(
        answerWrongJson,
        this.languages,
        true,
      );

      const groupAnswerWrongHtmls: AnswerHtml[] = [];

      for (const withLangGeneratedJson of withLangGeneratedJsons) {
        const answerEditor = new Editor({
          extensions: this.AEExtensions,
          content: withLangGeneratedJson.newJson,
        });

        const answerHtml = answerEditor.getHTML();

        groupAnswerWrongHtmls.push({
          languages: withLangGeneratedJson.languages,
          answer: answerHtml,
        });
      }

      answerWrongHtmls.push(groupAnswerWrongHtmls);
    }

    this.buildObjectiveQuestion.questionJson = questionJson;
    this.buildObjectiveQuestion.answerCorrectJson = answerCorrectJson;
    this.buildObjectiveQuestion.answerWrongJsons = answerWrongJsons;

    this.buildObjectiveQuestion.questionHtmls = questionHtmls;
    this.buildObjectiveQuestion.answerCorrectHtmls = answerCorrectHtmls;
    this.buildObjectiveQuestion.answerWrongHtmls = answerWrongHtmls;

    this.buildObjectiveQuestion.editorVersion = this.editorVersion;

    this.isOutputLoading = false;
  }

  async getSubjectiveOutput() {
    if (this.isOutputLoading) return false;
    this.isOutputLoading = true;

    const orignalJson = this.subQuestionEditor?.getJSON() as JSONContent;

    const questionJsons = await getModifiedJson(
      orignalJson,
      this.languages,
      true,
    );
    const pdfJsons = await getModifiedJson(orignalJson, this.languages, false);

    const questionHtmls: QuestionHtml[] = [];
    const pdfHtmls: PdfHtml[] = [];

    questionJsons.forEach(newJson => {
      const questionEditor = new Editor({
        extensions: this.QEExtensions,
        content: newJson.newJson,
      });

      const questionHtml = questionEditor.getHTML();

      questionHtmls.push({
        languages: newJson.languages,
        question: questionHtml,
      });
    });

    pdfJsons.forEach(newJson => {
      const questionEditor = new Editor({
        extensions: this.QEExtensions,
        content: newJson.newJson,
      });

      const answerEditor = new Editor({
        extensions: this.QEExtensions,
        content: newJson.answerJson,
      });

      const questionHtml = questionEditor.getHTML();
      const answerHtml = answerEditor.getHTML();

      pdfHtmls.push({
        languages: newJson.languages,
        question: questionHtml,
        answer: answerHtml,
      });
    });

    this.buildSubjectiveQuestion.questionJson = orignalJson;
    this.buildSubjectiveQuestion.questionHtmls = questionHtmls;
    this.buildSubjectiveQuestion.pdfHtmls = pdfHtmls;

    this.buildSubjectiveQuestion.editorVersion = this.editorVersion;

    this.isOutputLoading = false;
  }

  async createNewQuestion() {
    if (this.isAdmin) {
      const questionCreated = await this.saveQuestionForAdmin(
        SaveQuestionEvent.CreateDaft,
      );

      this.isRedirectPageLoading = true;

      if (questionCreated) {
        this.$router.push({
          path: 'question-editor',
          query: {
            'is-subjective': this.isSubjective ? 'true' : 'false',
            'is-admin': this.isAdmin ? 'true' : 'false',
            'question-id': `${questionCreated.id}`,
          },
        });
        this.$router.go(0);
      }
    } else {
      const questionCreated = await this.saveQuestionByUser();

      this.$sendMpEvent({ event: this.$mp_value.EditorCreateQuestion });

      this.isRedirectPageLoading = true;

      if (questionCreated && this.paperUuid) {
        this.$router.push({
          name: 'paper',
          params: { uuid: this.paperUuid },
        });
      }
    }
  }

  async updateQuestion() {
    if (this.question) {
      if (this.isAdmin) {
        await this.saveQuestionForAdmin(SaveQuestionEvent.Update);
        this.isRedirectPageLoading = true;
        this.$router.go(0);
      } else {
        await this.saveQuestionByUser();

        this.$sendMpEvent({ event: this.$mp_value.EditorUpdateQuestion });

        this.isRedirectPageLoading = true;

        if (this.paperUuid) {
          this.$router.push({
            name: 'paper',
            params: { uuid: this.paperUuid },
          });
        }
      }
    }
  }

  async deleteQuestion() {
    if (this.question) {
      await deleteQuestions({
        questionIds: [this.question.id],
        isSubjective: this.isSubjective,
      });

      this.isRedirectPageLoading = true;

      this.$router.push({
        path: 'question-editor',
        query: {
          'is-subjective': this.isSubjective ? 'true' : 'false',
          'is-admin': this.isAdmin ? 'true' : 'false',
        },
      });
      this.$router.go(0);
    }
  }

  async saveQuestionForAdmin(saveEvent: SaveQuestionEvent) {
    const saveQuestionsDetails: SaveQuestionParams = {
      event: saveEvent,
      questionId: this.question ? this.question.id : 0,
      isSubjective: this.isSubjective,
      buildQuestion: this.isSubjective
        ? this.buildSubjectiveQuestion
        : this.buildObjectiveQuestion,
    };
    const questionSaved = await saveQuestion(saveQuestionsDetails);

    if (questionSaved) {
      if (this.isDraft) {
        await this.unpublishQuestion(questionSaved.id);
      } else {
        await this.publishQuestion(questionSaved.id);
      }
    }

    return questionSaved;
  }

  async saveQuestionByUser() {
    if (this.isCreateNewQuestion) {
      if (!this.paperUuid) {
        this.$toasted.global.mainToast({
          message: 'No paper',
        });
        return;
      }

      const saveQuestionsDetails: AddQuestionByUserParams = {
        questionId: this.question ? this.question.id : 0,
        isSubjective: this.isSubjective,
        buildQuestion: this.isSubjective
          ? this.buildSubjectiveQuestion
          : this.buildObjectiveQuestion,
      };
      const questionSaved = await addQuestionByUser(saveQuestionsDetails);

      // NOTE: Add the new question created to paper
      if (questionSaved) {
        const addQuestionToPaperDetails: AddQuestionToPaperParams = {
          questionId: questionSaved.id,
          isSubjective: this.isSubjective,
          collectionUuid: this.paperUuid,
          // NOTE: Randomize again the the answer position
          answerPosition: randomizeObjAnswerPosition(
            questionSaved,
            this.isSubjective,
          ),
          languages: this.languages,
        };

        await addQuestionToPaper(addQuestionToPaperDetails);
      }

      return questionSaved;
    } else {
      if (!this.paperItemId || !this.paperUuid) {
        this.$toasted.global.mainToast({
          message: 'No paper item',
        });
        return;
      }

      const saveQuestionsDetails: UpdateQuestionByUserParams = {
        collectionItemId: this.paperItemId,
        questionId: this.question ? this.question.id : 0,
        isSubjective: this.isSubjective,
        buildQuestion: this.isSubjective
          ? this.buildSubjectiveQuestion
          : this.buildObjectiveQuestion,
      };
      const questionSaved = await updateQuestionByUser(saveQuestionsDetails);

      return questionSaved;
    }
  }

  async publishQuestion(questionId: number) {
    await publishQuestions({
      questionIds: [questionId],
      isSubjective: this.isSubjective,
    });
  }

  async unpublishQuestion(questionId: number) {
    await unpublishQuestions({
      questionIds: [questionId],
      isSubjective: this.isSubjective,
    });
  }
}
